import { useEffect, useState, useCallback } from "react";
import api, { formatApiError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";
import {
  Cards, NotePencil, CheckCircle, XCircle, Trash, Eye, Sparkle, Brain, ArrowRight,
} from "@phosphor-icons/react";

const CATS = {
  comprehension: "Compréhension", methode: "Méthode", calcul: "Calcul",
  lecture_consigne: "Lecture de consigne", vocabulaire: "Vocabulaire", gestion_temps: "Gestion du temps",
};

export default function Revisions() {
  const [tab, setTab] = useState("review");
  const [due, setDue] = useState([]);
  const [errors, setErrors] = useState([]);
  const [idx, setIdx] = useState(0);
  const [revealed, setRevealed] = useState(false);

  const loadReview = useCallback(() => {
    api.get("/learning/review/due").then((r) => { setDue(r.data); setIdx(0); setRevealed(false); });
  }, []);
  const loadErrors = useCallback(() => {
    api.get("/learning/error-notebook").then((r) => setErrors(r.data));
  }, []);

  useEffect(() => { loadReview(); loadErrors(); }, [loadReview, loadErrors]);

  const current = due[idx];

  const grade = async (correct) => {
    if (!current) return;
    try {
      await api.post(`/learning/review/${current.id}/grade`, { correct });
    } catch { /* ignore, advance anyway */ }
    setRevealed(false);
    if (idx + 1 < due.length) setIdx(idx + 1);
    else { toast.success("Séance de révision terminée ! 🎉"); loadReview(); loadErrors(); }
  };

  const setCategory = async (id, category) => {
    try {
      await api.post(`/learning/error/${id}/category`, { category });
      setErrors((prev) => prev.map((e) => (e.id === id ? { ...e, category } : e)));
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };

  const removeError = async (id) => {
    await api.delete(`/learning/error/${id}`);
    setErrors((prev) => prev.filter((e) => e.id !== id));
    loadReview();
  };

  return (
    <div className="space-y-8">
      <div className="animate-fade-up">
        <h1 className="font-display text-3xl font-extrabold sm:text-4xl">Révisions & carnet d'erreurs</h1>
        <p className="mt-1 text-ink/65">Transformez vos erreurs en acquis grâce à la révision espacée.</p>
      </div>

      <div className="flex gap-2">
        <button data-testid="tab-review" onClick={() => setTab("review")}
          className={`inline-flex items-center gap-2 rounded-full px-5 py-2 text-sm font-semibold transition ${tab === "review" ? "bg-terracotta text-white" : "bg-white text-ink/70 border border-border hover:bg-sand"}`}>
          <Cards weight="fill" size={16} /> Révisions du jour ({due.length})
        </button>
        <button data-testid="tab-notebook" onClick={() => setTab("notebook")}
          className={`inline-flex items-center gap-2 rounded-full px-5 py-2 text-sm font-semibold transition ${tab === "notebook" ? "bg-terracotta text-white" : "bg-white text-ink/70 border border-border hover:bg-sand"}`}>
          <NotePencil weight="fill" size={16} /> Carnet d'erreurs ({errors.length})
        </button>
      </div>

      {/* Flashcards de révision espacée */}
      {tab === "review" && (
        <div data-testid="review-panel">
          {!current ? (
            <div className="rounded-2xl border border-border bg-white p-10 text-center" data-testid="review-empty">
              <Brain weight="duotone" size={48} className="mx-auto text-forest" />
              <p className="mt-3 font-semibold">Aucune révision à faire pour l'instant 🎉</p>
              <p className="mt-1 text-sm text-muted-foreground">Vos erreurs de quiz et d'examens blancs apparaîtront ici pour être révisées au bon moment.</p>
            </div>
          ) : (
            <div className="mx-auto max-w-2xl">
              <div className="mb-3 flex items-center justify-between text-sm text-muted-foreground">
                <span>{current.subject}</span>
                <span>Carte {idx + 1} / {due.length}</span>
              </div>
              <div className="rounded-2xl border border-border bg-white p-6 animate-fade-up">
                <div className="flex items-center gap-2 text-xs font-semibold uppercase tracking-wide text-terracotta">
                  <Sparkle weight="fill" /> {CATS[current.category] || "À revoir"}
                </div>
                <h2 className="mt-3 font-display text-xl font-bold">{current.question}</h2>
                <div className="mt-4 space-y-2">
                  {current.options.map((opt, i) => {
                    const isCorrect = revealed && i === current.correct_index;
                    return (
                      <div key={i} className={`rounded-xl border-2 px-4 py-2.5 text-sm ${isCorrect ? "border-forest bg-forest/5 font-semibold text-forest" : "border-border"}`}>
                        <span className="mr-2 font-bold">{String.fromCharCode(65 + i)}.</span>{opt}
                        {isCorrect && <CheckCircle weight="fill" className="ml-2 inline text-forest" />}
                      </div>
                    );
                  })}
                </div>
                {revealed && current.explanation && (
                  <div className="mt-4 rounded-xl bg-cream px-4 py-3 text-sm text-ink/80"><b>Explication :</b> {current.explanation}</div>
                )}
              </div>

              {!revealed ? (
                <Button data-testid="reveal-btn" onClick={() => setRevealed(true)} className="mt-4 w-full rounded-full bg-forest text-white hover:bg-forest/90">
                  <Eye weight="fill" className="mr-1.5" /> Révéler la réponse
                </Button>
              ) : (
                <div className="mt-4 grid grid-cols-2 gap-3">
                  <Button data-testid="grade-wrong" onClick={() => grade(false)} variant="outline" className="rounded-full border-destructive text-destructive hover:bg-destructive/5">
                    <XCircle weight="fill" className="mr-1.5" /> Je ne savais pas
                  </Button>
                  <Button data-testid="grade-right" onClick={() => grade(true)} className="rounded-full bg-forest text-white hover:bg-forest/90">
                    <CheckCircle weight="fill" className="mr-1.5" /> Je savais
                  </Button>
                </div>
              )}
            </div>
          )}
        </div>
      )}

      {/* Carnet d'erreurs */}
      {tab === "notebook" && (
        <div className="space-y-3" data-testid="notebook-panel">
          {errors.length === 0 && (
            <div className="rounded-2xl border border-border bg-white p-10 text-center text-muted-foreground" data-testid="notebook-empty">
              Votre carnet est vide. Les questions ratées lors des quiz et examens blancs s'ajoutent ici automatiquement.
            </div>
          )}
          {errors.map((e) => (
            <div key={e.id} data-testid={`error-${e.id}`} className="rounded-2xl border border-border bg-white p-5">
              <div className="flex items-start justify-between gap-3">
                <div className="flex-1">
                  <div className="text-xs text-muted-foreground">{e.subject}
                    {e.status === "maitrise" && <span className="ml-2 rounded-full bg-forest/10 px-2 py-0.5 font-semibold text-forest">Maîtrisé ✓</span>}
                  </div>
                  <div className="mt-1 font-semibold">{e.question}</div>
                  <div className="mt-1 text-sm text-forest">Bonne réponse : {e.options[e.correct_index]}</div>
                </div>
                <button data-testid={`delete-${e.id}`} onClick={() => removeError(e.id)} className="rounded-lg p-2 text-muted-foreground hover:bg-sand hover:text-destructive">
                  <Trash size={18} />
                </button>
              </div>
              <div className="mt-3 flex items-center gap-2">
                <span className="text-xs text-muted-foreground">Type d'erreur :</span>
                <select data-testid={`category-${e.id}`} value={e.category} onChange={(ev) => setCategory(e.id, ev.target.value)}
                  className="rounded-lg border border-border bg-cream px-2 py-1 text-xs font-medium">
                  {Object.entries(CATS).map(([k, v]) => <option key={k} value={k}>{v}</option>)}
                </select>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
